import React, { useMemo, memo } from 'react';
import { Stack, Typography, LinearProgress } from '@mui/material';
import { StackProps } from '@mui/material/Stack';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import { IDebateRound } from 'shared/types/debates.interface';
import { getTimerFormat } from 'utils/card-helpers';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { selectRemainingTime } from 'redux/selectors/selectors';

type RoundProgressProps = StackProps & {
  currentRound: IDebateRound | null;
  totalRoundsNumber: number;
  isParticipantActive: boolean;
  lastRound?: IDebateRound | null;
};

export const RoundProgress: React.FC<RoundProgressProps> = memo(
  ({ currentRound, totalRoundsNumber, isParticipantActive, lastRound, ...props }) => {
    const { roundRemainingTime } = useAppSelector(selectRemainingTime);

    const totalTime = useMemo(
      () => Number(currentRound?.end_time) - Number(currentRound?.start_time),
      [currentRound],
    );

    const remainingTimeTraits = useMemo(() => {
      const percent =
        totalTime > 0 && isParticipantActive
          ? Math.floor((roundRemainingTime / totalTime) * 100)
          : 0;
      return {
        percent,
        color: percent > 70 ? 'myColors.green' : percent > 30 ? 'myColors.yellow' : 'myColors.red',
      };
    }, [roundRemainingTime, isParticipantActive, totalTime]);

    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        columnGap="24px"
        {...props}
      >
        <Typography variant="body2" color="secondary.main">
          {`${
            isParticipantActive
              ? currentRound?.speaker_round_number || 0
              : lastRound?.speaker_round_number || 0
          }/${totalRoundsNumber}`}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={remainingTimeTraits.percent}
          sx={(theme) => ({
            height: { md: '12px', xs: '8px' },
            borderRadius: { md: '12px', xs: '8px' },
            width: '100%',
            transform: 'scale(-1, 1)',
            [`&.${linearProgressClasses.colorPrimary}`]: {
              backgroundColor: theme.palette.myGrey.grey100,
            },
            [`& .${linearProgressClasses.bar}`]: {
              borderRadius: '12px',
              backgroundColor: remainingTimeTraits.color,
            },
          })}
          color="primary"
        />
        <Typography variant="body2" color="secondary.main">
          {isParticipantActive
            ? `${getTimerFormat(roundRemainingTime).min}:${getTimerFormat(roundRemainingTime).sec}`
            : '00:00'}
        </Typography>
      </Stack>
    );
  },
);
